import {Fragment, useContext, useEffect, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import Block from '../block';
import {WebsiteContext} from '../../index';
import {GOOGLE_REVIEWS_SETUP_MESSAGE, GOOGLE_REVIEWS_SETUP_STATES} from '../reviews-summary-block/google-reviews-setup-state';
import {fetchJsonSharedInFlight} from '../../utils/reviews-request-cache';

const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'});
};

const Stars = ({rating}) => {
    const r = Math.round(Number(rating || 0));
    return (
        <span aria-label={`${r} out of 5`} className="text-yellow-500">
            {'★★★★★'.slice(0, r)}{'☆☆☆☆☆'.slice(0, 5 - r)}
        </span>
    );
};

const buildPlaceholderItems = (count) => {
    return Array.from({length: count}).map((_, idx) => ({
        placeholder: true,
        author: 'Google Reviewer',
        rating: 5,
        text: '',
        time: new Date(Date.now() - idx * 86400000).toISOString(),
    }));
};

export default function ReviewsListBlock(props) {
    const [items, setItems] = useState([]);
    const [mapsUrl, setMapsUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalReview, setModalReview] = useState(null);
    const context = useContext(WebsiteContext);
    const isBuilder = !!context?.builder;
    const isPicker = !!context?.picker;
    const isEditor = isBuilder || isPicker;
    const reviewsSetupState = String(context?.reviewsSetup?.google || GOOGLE_REVIEWS_SETUP_STATES.unknown).trim().toLowerCase();
    const showSetupWarning = isEditor && reviewsSetupState === GOOGLE_REVIEWS_SETUP_STATES.notConfigured;
    const isCheckingSetup = !!context?.reviewsSetupLoading?.google;

    const provider = props.values?.provider || 'places';
    const mode = props.values?.mode || 'group';
    const scope = props.values?.scope || 'website';
    const groupKey = props.values?.groupKey || 'all';
    const sourceKey = props.values?.sourceKey;
    const sourceKeys = Array.isArray(props.values?.sourceKeys)
        ? props.values.sourceKeys.filter(Boolean)
        : [];
    const sourceKeysToken = sourceKeys.join('|');
    const sort = props.values?.sort || 'newest';
    const limit = props.values?.limit ?? 5;
    const minRating = props.values?.minRating;
    const variant = props.values?.variant || 'cards';

    useEffect(() => {
        if (showSetupWarning) {
            setItems([]);
            setMapsUrl(null);
            setIsLoading(false);
            return;
        }

        const controller = new AbortController();
        let active = true;

        const fetchReviews = async () => {
            try {
                setIsLoading(true);
                const qs = new URLSearchParams();
                qs.set('provider', provider);
                qs.set('sort', sort);
                qs.set('limit', String(limit));
                if (scope) qs.set('scope', scope);
                if (minRating !== undefined && minRating !== null && minRating !== '') {
                    qs.set('minRating', String(minRating));
                }

                let listData = null;
                if (mode === 'sources') {
                    try {
                        listData = await fetchJsonSharedInFlight({
                            url: `/api/v2/reviews/list?${qs.toString()}`,
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: sourceKeys,
                            signal: controller.signal
                        });
                    } catch (e) {
                        if (e.name === 'AbortError') {
                            throw e;
                        }
                    }
                } else {
                    const url = mode === 'single'
                        ? `/api/v2/reviews/list?${qs.toString()}&sourceKey=${encodeURIComponent(sourceKey || '')}`
                        : `/api/v2/reviews/group/${encodeURIComponent(groupKey)}/list?${qs.toString()}`;
                    try {
                        listData = await fetchJsonSharedInFlight({
                            url,
                            signal: controller.signal
                        });
                    } catch (e) {
                        if (e.name === 'AbortError') {
                            throw e;
                        }
                    }
                }

                if (listData && active) {
                    setItems(Array.isArray(listData) ? listData : []);
                }

                // Fetch a summary to obtain a stable googleMapsUri for attribution/linking
                let summary = null;
                if (mode === 'sources') {
                    try {
                        summary = await fetchJsonSharedInFlight({
                            url: `/api/v2/reviews/group/summary?provider=${encodeURIComponent(provider)}`,
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: sourceKeys,
                            signal: controller.signal
                        });
                    } catch (e) {
                        if (e.name === 'AbortError') {
                            throw e;
                        }
                    }
                } else if (mode === 'single') {
                    try {
                        summary = await fetchJsonSharedInFlight({
                            url: `/api/v2/reviews/summary?provider=${encodeURIComponent(provider)}&sourceKey=${encodeURIComponent(sourceKey || '')}`,
                            signal: controller.signal
                        });
                    } catch (e) {
                        if (e.name === 'AbortError') {
                            throw e;
                        }
                    }
                } else {
                    try {
                        summary = await fetchJsonSharedInFlight({
                            url: `/api/v2/reviews/group/${encodeURIComponent(groupKey)}/summary?provider=${encodeURIComponent(provider)}&scope=${encodeURIComponent(scope)}`,
                            signal: controller.signal
                        });
                    } catch (e) {
                        if (e.name === 'AbortError') {
                            throw e;
                        }
                    }
                }

                if (summary && active) {
                    setMapsUrl(summary?.mapsUrl || null);
                }
            } catch (e) {
                if (e.name !== 'AbortError') console.log(e);
            } finally {
                if (active) setIsLoading(false);
            }
        };

        if (mode === 'single' && !sourceKey) return () => controller.abort();
        if (mode === 'sources' && sourceKeys.length === 0) return () => controller.abort();

        fetchReviews();
        return () => {
            active = false;
            controller.abort();
        };
    }, [provider, mode, scope, groupKey, sourceKey, sourceKeysToken, sort, limit, minRating, showSetupWarning]);

    if (showSetupWarning) {
        const onRecheck = () => {
            if (typeof context?.onRecheckGoogleReviewsSetup === 'function') {
                context.onRecheckGoogleReviewsSetup();
                return;
            }

            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('cx:google-reviews-settings-updated'));
            }
        };

        return (
            <Block {...props} className={props.className}>
                <div className="mx-auto max-w-7xl section">
                    <div className="inline-flex flex-col rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        <div>{GOOGLE_REVIEWS_SETUP_MESSAGE}</div>
                        <button
                            type="button"
                            className="mt-1 inline-flex w-fit items-center rounded border border-amber-300 bg-white px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={onRecheck}
                            disabled={isCheckingSetup}
                        >
                            {isCheckingSetup ? 'Rechecking...' : 'Recheck'}
                        </button>
                    </div>
                </div>
            </Block>
        );
    }

    const showPlaceholder = (isBuilder || isPicker || isLoading) && items.length === 0;
    const placeholderCount = Math.max(1, Math.min(Number(limit) || 1, 4));
    const displayItems = items.length ? items : (showPlaceholder ? buildPlaceholderItems(placeholderCount) : []);
    if (!displayItems.length) return null;

    const columns = variant === 'quote' ? 1 : 2;
    const rows = Math.ceil(displayItems.length / columns);
    const estimatedCardHeight = variant === 'quote' ? 120 : 184;
    const minHeight = showPlaceholder ? rows * estimatedCardHeight + 32 : undefined;

    const openModal = (review) => {
        setModalReview(review);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const gridClass = variant === 'quote'
        ? 'space-y-6'
        : 'grid grid-cols-1 md:grid-cols-2 gap-4';

    return (
        <Block {...props} className={props.className}>
            <div className="mx-auto max-w-7xl section">
                <div style={minHeight ? {minHeight} : undefined} aria-busy={isLoading ? 'true' : 'false'}>
                    <div className={gridClass}>
                        {displayItems.map((r, idx) => {
                            const isPlaceholder = r.placeholder === true;
                            const hasLongText = !isPlaceholder && r.text && r.text.length > 160;
                            const cardClass = variant === 'quote'
                                ? 'border-l-4 border-gray-200 pl-4 min-h-[120px]'
                                : 'border border-gray-200 rounded-lg p-4 bg-white shadow-sm min-h-[184px]';

                            return (
                                <div
                                    key={`${r.sourceKey || 'source'}_${idx}`}
                                    className={cardClass}
                                >
                                    {isPlaceholder ? (
                                        <div className="animate-pulse space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div className="h-3 bg-gray-200 rounded w-32" />
                                                <div className="h-3 bg-gray-200 rounded w-20" />
                                            </div>
                                            <div className="h-3 bg-gray-200 rounded w-24" />
                                            <div className="space-y-2">
                                                <div className="h-3 bg-gray-200 rounded w-full" />
                                                <div className="h-3 bg-gray-200 rounded w-5/6" />
                                                <div className="h-3 bg-gray-200 rounded w-4/6" />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm font-semibold text-gray-900">{r.author || 'Google review'}</div>
                                                <div className="text-xs text-gray-500">{formatDate(r.time)}</div>
                                            </div>
                                            <div className="mt-1 text-sm">
                                                <Stars rating={r.rating}/>
                                                <span className="ml-2 text-gray-600">{Number(r.rating || 0).toFixed(0)}/5</span>
                                            </div>
                                            {r.text ? (
                                                <div className={`mt-3 text-sm text-gray-700 leading-relaxed ${variant === 'quote' ? 'line-clamp-3' : 'line-clamp-4'}`}>
                                                    {r.text}
                                                </div>
                                            ) : null}
                                            {hasLongText ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openModal(r)}
                                                    className="mt-2 text-xs font-semibold text-primary-600 hover:underline"
                                                >
                                                    Read full review
                                                </button>
                                            ) : null}
                                        </>
                                    )}
                                </div>
                            );
                        })}

                        <div className={variant === 'quote' ? '' : 'md:col-span-2'}>
                            <div className="text-xs text-gray-500">
                                {mapsUrl && !showPlaceholder ? (
                                    <a href={mapsUrl} target="_blank" rel="noreferrer" className="hover:underline">
                                        Reviews powered by Google
                                    </a>
                                ) : (
                                    'Reviews powered by Google'
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Transition appear show={isModalOpen} as={Fragment}
                        afterLeave={() => setModalReview(null)}>
                <Dialog
                    as="div"
                    className="relative z-10"
                    onClose={closeModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/30"/>
                    </Transition.Child>
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <div className="relative">
                                <Dialog.Panel
                                    className="bg-white rounded-theme shadow-lg max-w-lg w-full mx-auto p-6 max-h-[75vh] overflow-y-auto">
                                    <Dialog.Title
                                        as="h5"
                                        className="text-center text-gray-900 mb-3"
                                    >
                                        Google review
                                    </Dialog.Title>
                                    {modalReview ? (
                                        <>
                                            <div className="flex items-center justify-between text-sm text-gray-600">
                                                <div className="font-semibold text-gray-900">{modalReview.author || 'Reviewer'}</div>
                                                <div>{formatDate(modalReview.time)}</div>
                                            </div>
                                            <div className="mt-2 text-sm">
                                                <Stars rating={modalReview.rating}/>
                                                <span className="ml-2 text-gray-600">{Number(modalReview.rating || 0).toFixed(0)}/5</span>
                                            </div>
                                            {modalReview.text ? (
                                                <div className="mt-3 text-sm text-gray-700 whitespace-pre-line">
                                                    {modalReview.text}
                                                </div>
                                            ) : null}
                                        </>
                                    ) : null}
                                </Dialog.Panel>
                                <button
                                    onClick={closeModal}
                                    className="absolute -bottom-1 mt-4 mx-auto left-1/2 transform -translate-x-1/2 border-2 border-gray-300 bg-white rounded-full shadow-lg overflow-hidden"
                                >
                                    <svg
                                        className="h-8 w-8 stroke-gray-800 bg-gray-100 p-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>
        </Block>
    );
}
